/*Store locator map pane restore (ECSR-642).

The store locator app renders List and Map as two exclusive views and its tab
handlers reflect that: choosing List sets an inline display:none on the map pane.
At the widths where the app's own stylesheet lays List and Map out side by side,
that inline rule wins over the stylesheet and the map pane collapses to nothing,
leaving its container as blank white space for the rest of the session. Only a
fresh page load brings it back.

This restores the pane by clearing the inline rule and letting the app's
stylesheet decide, which keeps the two layouts straight without this file having
to know the app's breakpoint: where the stylesheet wants the map hidden the
inline rule is put back untouched, so the mobile exclusive views keep working.*/

(function () {
  'use strict';

  const INIT_FLAG = '__scaStoreLocatorMapRestore';
  const MAP_ID = 'bh-sl-map';
  const RESIZE_DEBOUNCE_MS = 150;

  /*The theme editor re-runs a section's assets on every re-render, so without
    this the observers below would stack up over an editing session.*/
  if (window[INIT_FLAG]) {
    return;
  }
  window[INIT_FLAG] = true;

  let boundMap = null;

  const paneObserver = new window.MutationObserver(syncMapPane);

  function observePane() {
    paneObserver.observe(boundMap, {
      attributes: true,
      attributeFilter: ['style']
    });
  }

  function syncMapPane() {
    if (boundMap && !boundMap.isConnected) {
      /*Safety net for the one rebuild the parent watch below cannot see: the
        whole container being replaced, which takes the watched parent with it.*/
      bindCurrentMap();
      return;
    }

    if (!boundMap || boundMap.style.display !== 'none') {
      return;
    }

    /*Stop observing first: the probe below writes to the same style attribute
      this observer watches, and disconnecting drops those queued records so the
      callback cannot re-enter itself.*/
    paneObserver.disconnect();

    boundMap.style.display = '';
    if (window.getComputedStyle(boundMap).display === 'none') {
      /*Stylesheet agrees the map should be hidden here, so this is the mobile
        exclusive-view layout and the app's inline rule was correct.*/
      boundMap.style.display = 'none';
    }

    observePane();
  }

  /*Binds to whichever map pane is currently in the document, so a pane that
    arrives late — or one the app tears down and rebuilds on a fresh search — is
    picked up rather than leaving this observing a detached node.*/
  function bindCurrentMap() {
    const map = document.getElementById(MAP_ID);

    if (!map) {
      boundMap = null;
      paneObserver.disconnect();
      parentObserver.disconnect();
      watchDocument();
      return;
    }

    if (map === boundMap) {
      return;
    }

    boundMap = map;
    paneObserver.disconnect();
    observePane();

    /*Now the pane is found, stop searching the whole document and watch only
      its parent's own children for a rebuild. The map's interior churns
      constantly while a shopper pans or zooms — that is the one interaction
      this page exists for — and a document-wide subtree watch would record
      every one of those mutations only to discard them.*/
    documentObserver.disconnect();
    parentObserver.disconnect();
    if (map.parentNode) {
      parentObserver.observe(map.parentNode, { childList: true });

      /*Watch a level up as well. Replacing the container wholesale takes the
        watched parent with it, so it mutates nothing and would otherwise go
        unseen, leaving this bound to an orphan while the app talks to a new
        pane. Neither level churns — the interior that does is below both.*/
      if (map.parentNode.parentNode) {
        parentObserver.observe(map.parentNode.parentNode, { childList: true });
      }
    }

    syncMapPane();
  }

  const documentObserver = new window.MutationObserver(bindCurrentMap);
  const parentObserver = new window.MutationObserver(bindCurrentMap);

  function watchDocument() {
    documentObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  let resizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(syncMapPane, RESIZE_DEBOUNCE_MS);
  });

  /*A section re-render in the theme editor rebuilds the app block above both
    watched levels, so neither observer sees it and the init guard above stops a
    fresh copy of this script recovering. Rebinding on the event terminates that
    regress at the section boundary rather than by climbing further up the DOM.
    Never fires on the storefront.*/
  document.addEventListener('shopify:section:load', bindCurrentMap);

  /*Binds if the app has already rendered, and starts watching for it if not.*/
  bindCurrentMap();
})();
